import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Plus,
  Pencil,
  Trash2,
  Save,
  X as XIcon,
  Star,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "starters", label: "Entrées" },
  { id: "potence", label: "La Potence" },
  { id: "meats", label: "Nos Viandes" },
  { id: "fish", label: "Poissons" },
  { id: "desserts", label: "Desserts" },
];

const emptyForm = (category) => ({
  category,
  name_fr: "",
  name_en: "",
  desc_fr: "",
  desc_en: "",
  price: 0,
  suffix: "",
  signature: false,
  order: 0,
});

const ItemForm = ({ initial, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(initial);
  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]:
        type === "checkbox"
          ? checked
          : name === "price" || name === "order"
            ? Number(value)
            : value,
    }));
  };
  return (
    <form
      data-testid="menu-item-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(form);
      }}
      className="bg-obsidian border border-copper p-5 grid grid-cols-1 md:grid-cols-2 gap-4"
    >
      <div className="md:col-span-2 grid grid-cols-2 gap-4">
        <div>
          <label className="overline block mb-2">Catégorie</label>
          <select
            data-testid="form-category"
            name="category"
            value={form.category}
            onChange={onChange}
            className="input-elegant bg-obsidian"
          >
            {CATEGORIES.map((c) => (
              <option key={c.id} value={c.id} className="bg-obsidian">
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="overline block mb-2">Ordre</label>
          <input
            data-testid="form-order"
            type="number"
            name="order"
            value={form.order}
            onChange={onChange}
            className="input-elegant"
          />
        </div>
      </div>
      <div>
        <label className="overline block mb-2">Nom (FR) *</label>
        <input
          data-testid="form-name-fr"
          required
          name="name_fr"
          value={form.name_fr}
          onChange={onChange}
          className="input-elegant"
        />
      </div>
      <div>
        <label className="overline block mb-2">Nom (EN) *</label>
        <input
          data-testid="form-name-en"
          required
          name="name_en"
          value={form.name_en}
          onChange={onChange}
          className="input-elegant"
        />
      </div>
      <div>
        <label className="overline block mb-2">Description (FR)</label>
        <input
          data-testid="form-desc-fr"
          name="desc_fr"
          value={form.desc_fr}
          onChange={onChange}
          className="input-elegant"
        />
      </div>
      <div>
        <label className="overline block mb-2">Description (EN)</label>
        <input
          data-testid="form-desc-en"
          name="desc_en"
          value={form.desc_en}
          onChange={onChange}
          className="input-elegant"
        />
      </div>
      <div>
        <label className="overline block mb-2">Prix (CHF) *</label>
        <input
          data-testid="form-price"
          required
          type="number"
          step="0.5"
          min="0"
          name="price"
          value={form.price}
          onChange={onChange}
          className="input-elegant"
        />
      </div>
      <div>
        <label className="overline block mb-2">Suffixe (ex: / pers.)</label>
        <input
          data-testid="form-suffix"
          name="suffix"
          value={form.suffix}
          onChange={onChange}
          className="input-elegant"
          placeholder="/ pers."
        />
      </div>
      <label className="md:col-span-2 flex items-center gap-3 text-sm text-ivory/70 cursor-pointer">
        <input
          data-testid="form-signature"
          type="checkbox"
          name="signature"
          checked={form.signature}
          onChange={onChange}
          className="w-4 h-4 accent-copper"
        />
        Plat signature (étoile cuivre)
      </label>
      <div className="md:col-span-2 flex justify-end gap-3 pt-2">
        <button
          type="button"
          data-testid="form-cancel"
          onClick={onCancel}
          className="btn-secondary"
          style={{ padding: "0.6rem 1.2rem", fontSize: "0.7rem" }}
        >
          <XIcon size={14} /> Annuler
        </button>
        <button
          type="submit"
          data-testid="form-save"
          disabled={saving}
          className="btn-primary disabled:opacity-60"
          style={{ padding: "0.6rem 1.2rem", fontSize: "0.7rem" }}
        >
          <Save size={14} /> {saving ? "Sauvegarde..." : "Sauvegarder"}
        </button>
      </div>
    </form>
  );
};

const AdminMenu = () => {
  const token = localStorage.getItem("grizzly_admin_token");
  const authHeader = { Authorization: `Bearer ${token}` };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCat, setOpenCat] = useState("potence");
  const [editingId, setEditingId] = useState(null);
  const [creatingCat, setCreatingCat] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/menu`, { headers: authHeader });
      setItems(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleCreate = async (form) => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/menu`, form, {
        headers: authHeader,
      });
      setItems((arr) => [...arr, res.data]);
      setCreatingCat(null);
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (form) => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/admin/menu/${form.id}`, form, {
        headers: authHeader,
      });
      setItems((arr) => arr.map((it) => (it.id === form.id ? res.data : it)));
      setEditingId(null);
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur lors de la modification");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer définitivement cet article ?")) return;
    try {
      await axios.delete(`${API}/admin/menu/${id}`, { headers: authHeader });
      setItems((arr) => arr.filter((it) => it.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div data-testid="admin-menu" className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-ivory/55">
          {items.length} articles · répartis sur {CATEGORIES.length} catégories
        </p>
      </div>

      {loading ? (
        <div className="text-center py-20 text-ivory/50">Chargement...</div>
      ) : (
        CATEGORIES.map((cat) => {
          const catItems = items
            .filter((it) => it.category === cat.id)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          const isOpen = openCat === cat.id;
          return (
            <div
              key={cat.id}
              data-testid={`admin-cat-${cat.id}`}
              className="bg-surface border border-edge"
            >
              <button
                data-testid={`toggle-cat-${cat.id}`}
                onClick={() => setOpenCat(isOpen ? null : cat.id)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-edge/30 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <span className="font-serif text-xl text-ivory">
                    {cat.label}
                  </span>
                  <span className="overline">{catItems.length} articles</span>
                </div>
                {isOpen ? (
                  <ChevronUp size={18} className="text-copper" />
                ) : (
                  <ChevronDown size={18} className="text-ivory/40" />
                )}
              </button>

              {isOpen && (
                <div className="border-t border-edge p-5 space-y-3">
                  {catItems.map((item) =>
                    editingId === item.id ? (
                      <ItemForm
                        key={item.id}
                        initial={item}
                        saving={saving}
                        onSave={handleUpdate}
                        onCancel={() => setEditingId(null)}
                      />
                    ) : (
                      <div
                        key={item.id}
                        data-testid={`item-row-${item.id}`}
                        className="bg-obsidian border border-edge p-4 grid grid-cols-12 gap-3 items-center"
                      >
                        <div className="col-span-12 md:col-span-5">
                          <div className="flex items-center gap-2">
                            <span className="font-serif text-lg text-ivory">
                              {item.name_fr}
                            </span>
                            {item.signature && (
                              <Star
                                size={12}
                                className="text-copper fill-copper"
                              />
                            )}
                          </div>
                          <p className="text-xs text-ivory/40 italic">
                            {item.name_en}
                          </p>
                          {item.desc_fr && (
                            <p className="text-xs text-ivory/55 mt-1">
                              {item.desc_fr}
                            </p>
                          )}
                        </div>
                        <div className="col-span-6 md:col-span-3 font-serif text-lg text-copper">
                          {item.price} <span className="text-xs">CHF</span>
                          {item.suffix && (
                            <span className="text-xs text-ivory/55 ml-1">
                              {item.suffix}
                            </span>
                          )}
                        </div>
                        <div className="col-span-3 md:col-span-2 overline">
                          #{item.order}
                        </div>
                        <div className="col-span-3 md:col-span-2 flex justify-end gap-2">
                          <button
                            data-testid={`edit-${item.id}`}
                            onClick={() => setEditingId(item.id)}
                            className="p-2 border border-edge text-ivory/70 hover:text-copper hover:border-copper transition-colors"
                            title="Modifier"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            data-testid={`delete-${item.id}`}
                            onClick={() => handleDelete(item.id)}
                            className="p-2 border border-edge text-ivory/55 hover:text-red-400 hover:border-red-400/40 transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ),
                  )}

                  {creatingCat === cat.id ? (
                    <ItemForm
                      initial={emptyForm(cat.id)}
                      saving={saving}
                      onSave={handleCreate}
                      onCancel={() => setCreatingCat(null)}
                    />
                  ) : (
                    <button
                      data-testid={`add-item-${cat.id}`}
                      onClick={() => {
                        setEditingId(null);
                        setCreatingCat(cat.id);
                      }}
                      className="w-full flex items-center justify-center gap-2 py-3 border border-dashed border-edge text-ivory/55 hover:text-copper hover:border-copper transition-colors text-xs uppercase tracking-[0.2em]"
                    >
                      <Plus size={14} /> Ajouter un article
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default AdminMenu;
